<template>
  <div>
    <v-dialog v-model="dialog" persistent max-width="500">
      <v-card>
        <!-- TITLE -->
        <v-card-title class="redColor d-flex text-white">
          <span v-if="edit" class="headline">
            {{ $t("users.form.edit_user") }}
          </span>
          <span v-else class="headline">
            {{ $t("users.form.create_user") }}
          </span>
          <v-spacer />
          <v-icon @click="close"> mdi-close </v-icon>
        </v-card-title>

        <!-- FORM -->
        <v-card-text>
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-container>
              <v-row>
                <!-- NAME -->
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="user.name"
                    :rules="rules.requiredLetters"
                    :label="$t('users.form.name')"
                    color="red darken-1"
                    prepend-icon="mdi-account"
                  />
                </v-col>

                <!-- SURNAME -->
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="user.surname"
                    :rules="rules.requiredLetters"
                    :label="$t('users.form.surname')"
                    color="red darken-1"
                    prepend-icon="mdi-account"
                  />
                </v-col>

                <!-- EMAIL -->
                <v-col cols="12">
                  <v-text-field
                    v-model="user.email"
                    :rules="rules.email"
                    :label="$t('users.form.email')"
                    color="red darken-1"
                    prepend-icon="mdi-email"
                  />
                </v-col>

                <!-- PASSWORD -->
                <v-col cols="12">
                  <v-text-field
                    v-model="user.password"
                    :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    :label="$t('users.form.password')"
                    color="red darken-1"
                    prepend-icon="mdi-lock"
                    :type="showPassword ? 'text' : 'password'"
                    :rules="edit ? rules.password : rules.requiredPassword"
                    @click:append="showPassword = !showPassword"
                  />
                </v-col>

                <!-- PHONE -->
                <v-col cols="12">
                  <v-text-field
                    v-model="user.phone"
                    :rules="rules.requiredNumbers"
                    :label="$t('users.form.phone')"
                    color="red darken-1"
                    prepend-icon="mdi-phone"
                  />
                </v-col>

                <!-- ADDRESS -->
                <v-col cols="12">
                  <v-text-field
                    v-model="user.address"
                    :rules="rules.required"
                    :label="$t('users.form.address')"
                    color="red darken-1"
                    prepend-icon="mdi-map-marker"
                  />
                </v-col>

                <!-- STATE -->
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="state"
                    :rules="rules.required"
                    :items="states"
                    :label="$t('users.form.state')"
                    color="red darken-1"
                    item-title="name"
                    item-value="name"
                    prepend-icon="mdi-earth"
                    return-object
                  />
                </v-col>

                <!-- CITY -->
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="user.city"
                    :rules="rules.required"
                    :items="cities"
                    :label="$t('users.form.city')"
                    color="red darken-1"
                    item-title="name"
                    item-value="name"
                    prepend-icon="mdi-city"
                  />
                </v-col>

                <!-- DATE OF BIRTH -->
                <v-col cols="12">
                  <v-text-field
                    v-model="user.date"
                    :rules="rules.required"
                    :label="$t('users.form.date_of_birth')"
                    color="red darken-1"
                    prepend-icon="mdi-calendar"
                    type="date"
                  />
                </v-col>

                <!-- STATUS -->
                <v-col cols="12">
                  <v-text-field
                    v-model="user.status"
                    :rules="rules.requiredLetters"
                    :label="$t('users.form.status')"
                    color="red darken-1"
                    prepend-icon="mdi-poll"
                  />
                </v-col>

                <!-- ROLE (Admin / Employee / SuperAdmin) -->
                <v-col cols="12">
                  <v-select
                    v-if="
                      $store.getters.role === 'LekoAdmin' ||
                      $store.getters.role === 'LekoEmployee' ||
                      $store.getters.role === 'SuperAdmin'
                    "
                    v-model="user.role"
                    :items="lekoRoles"
                    :label="$t('users.form.role')"
                    color="red darken-1"
                    prepend-icon="mdi-poll"
                    item-title="title"
                    item-value="value"
                  />

                  <!-- ROLE (InstitutionAdmin) -->
                  <v-select
                    v-else-if="$store.getters.role === 'InstitutionAdmin'"
                    v-model="user.role"
                    :items="ownerRoles"
                    :label="$t('users.form.role')"
                    color="red darken-1"
                    prepend-icon="mdi-poll"
                    item-title="title"
                    item-value="value"
                  />
                </v-col>

                <!-- PORTAL PERMISSIONS (SuperAdmin only for now) -->
                <v-col
                  v-if="edit && $store.getters.role === 'SuperAdmin'"
                  cols="12"
                >
                  <v-switch
                    v-model="whatsappInboxPermission"
                    color="red darken-1"
                    :label="$t('users.form.permission_whatsapp_inbox')"
                    :hint="$t('users.form.permission_whatsapp_inbox_hint')"
                    persistent-hint
                  />
                </v-col>

                <!-- INSTITUTION -->
                <v-col cols="12">
                  <v-select
                    v-if="
                      $store.getters.role === 'LekoAdmin' ||
                      $store.getters.role === 'LekoEmployee' ||
                      $store.getters.role === 'SuperAdmin'
                    "
                    v-model="user.institution_id"
                    :rules="rules.required"
                    :items="institutions"
                    :label="$t('users.form.select_institution')"
                    color="red darken-1"
                    item-title="title"
                    item-value="_id"
                    prepend-icon="mdi-domain"
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>

        <!-- ACTIONS -->
        <v-card-actions>
          <v-spacer />
          <v-btn color="red darken-1" variant="outlined" @click="close">
            {{ $t("users.form.close") }}
          </v-btn>
          <v-btn
            :disabled="!valid"
            :loading="loading"
            color="green-darken-1"
            variant="outlined"
            @click="save"
          >
            {{ $t("users.form.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="errorDialog" width="auto">
      <v-card>
        <v-card-title class="text-h6">
          <v-alert
            class="mb-0"
            :value="true"
            type="error"
            variant="tonal"
            border="bottom"
          >
            {{ errorMessage }}
          </v-alert>
        </v-card-title>

        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="errorDialog = false">
            {{ $t("close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import getRequest from "../../api/getRequest";
import putRequest from "../../api/putRequest";
import postRequest from "../../api/postRequest";

export default {
  data: () => ({
    dialog: false,
    edit: null,
    valid: false,
    loading: false,
    loadingState: false,
    loadingCity: false,
    loadingInstitution: false,
    user: {},
    state: null,
    states: [],
    cities: [],
    institutions: [],
    whatsappInboxPermission: false,
    password: "",
    showPassword: false,
    rules: {
      required: [(v) => !!v || "Please fill this field"],
      requiredLetters: [
        (v) => !!v || "Please fill this field",
        (v) => /[A-Za-z]/.test(v) || "Only letters are allowed",
      ],
      password: [
        (v) => {
          if (v)
            return (
              /(?=.*\d)(?=.*[!@#$%^&*])/.test(v) ||
              "Password must contain at least one number and one special character"
            );
          return true;
        },
      ],
      requiredPassword: [
        (v) => !!v || "Please fill this field",
        (v) =>
          /(?=.*\d)(?=.*[!@#$%^&*])/.test(v) ||
          "Password must contain at least one number and one special character",
      ],
      requiredNumbers: [
        (v) => !!v || "Please fill this field",
        (v) => /^[0-9]*$/.test(v) || "Only numbers are allowed",
      ],
      email: [
        (v) => !!v || "E-mail is required",
        (v) => /.+@.+/.test(v) || "E-mail must be valid",
      ],
    },
    lekoRoles: [
      {
        title: "Leko Digiatlization Employee ",
        value: "LekoEmployee",
      },
      {
        title: "Institution Admin",
        value: "InstitutionAdmin",
      },
      {
        title: "Institution Manager",
        value: "InstitutionManager",
      },
      {
        title: "Operator",
        value: "User",
      },
      {
        title: "Ebay Operator",
        value: "EbayOperator",
      },
    ],
    ownerRoles: [
      {
        title: "Institution Manager",
        value: "InstitutionManager",
      },
      {
        title: "Operator",
        value: "User",
      },
      {
        title: "Ebay Operator",
        value: "EbayOperator",
      },
    ],
    errorDialog: false,
    errorMessage: "",
  }),
  watch: {
    state: {
      handler(value) {
        if (value) {
          this.user.state = value.name;
          this.getCities(value._id);
        }
      },
    },
  },
  mounted() {
    this.getLocale();
  },
  methods: {
    getLocale() {
      this.lekoRoles[0].title = this.$t(
        "users.table.leko_digitalization_employee",
      );
      this.lekoRoles[1].title = this.$t("users.table.institution_admin");
      this.lekoRoles[2].title = this.$t("users.table.institution_manager");
      this.lekoRoles[3].title = this.$t("users.table.operator");
      this.lekoRoles[4].title = this.$t("users.table.ebay_operator");

      this.ownerRoles[0].title = this.$t("users.table.institution_manager");
      this.ownerRoles[1].title = this.$t("users.table.operator");
      this.ownerRoles[2].title = this.$t("users.table.ebay_operator");
    },
    async open(edit, data) {
      this.edit = edit;
      if (data) {
        this.user = { ...data };
        this.user.password = "";
        if (!Array.isArray(this.user.permissions)) {
          this.user.permissions = [];
        }
        this.whatsappInboxPermission = this.user.permissions.includes('whatsapp_inbox');
        if (this.user.date) {
          this.user.date = this.formatDateToDisplay(this.user.date);
        }
      }
      this.dialog = true;
      await this.getStates();

      if (data && data.state) {
        // Find the full state object from the loaded states list
        const selectedState = this.states.find((s) => s.name === data.state);
        if (selectedState) {
          this.state = selectedState;
          this.getCities(selectedState._id);
        }
      }
      if (
        this.$store.getters.role === "LekoAdmin" ||
        this.$store.getters.role === "LekoEmployee" ||
        this.$store.getters.role === "SuperAdmin"
      ) {
        this.getInstitutions();
      }
    },
    close() {
      if (this.$refs.form) this.$refs.form.resetValidation();
      this.dialog = false;
      this.user = {};
      this.state = null;
      this.edit = null;
      this.institutions = [];
      this.states = [];
      this.cities = [];
      // this.$emit("close");
    },
    save() {
      if (this.$refs.form.validate()) {
        this.loading = true;
        if (this.$store.getters.role === "InstitutionAdmin") {
          this.user.institution_id = this.$store.getters.institutionId;
        }
        if (this.edit === true) {
          const payload = { ...this.user };
          const canEditPermissions = [
            'SuperAdmin',
          ].includes(this.$store.getters.role);
          if (canEditPermissions) {
            const perms = Array.isArray(this.user.permissions)
              ? [...this.user.permissions]
              : [];
            const withoutInbox = perms.filter((p) => p !== 'whatsapp_inbox');
            payload.permissions = this.whatsappInboxPermission
              ? [...withoutInbox, 'whatsapp_inbox']
              : withoutInbox;
          } else {
            delete payload.permissions;
          }
          putRequest("/user/", `id=${this.user._id}`, payload)
            .then((response) => {
              this.loading = false;
              this.$emit("saved");
              this.$emit("close");
              this.close();
            })
            .catch((error) => {
              this.loading = false;
              this.$emit("error", error);
              this.close();
            });
        } else {
          postRequest
            .object("/user/", this.user)
            .then((response) => {
              this.loading = false;
              this.$emit("close");
              this.$emit("saved");
              this.close();
            })
            .catch((error) => {
              this.loading = false;
              this.errorMessage = error.response.data || "An error occurred";
              this.errorDialog = true;
              this.close();
            });
        }
      }
    },
    getInstitutions() {
      this.loadingInstitution = true;
      getRequest("/institution/")
        .then((response) => {
          this.institutions = response.institutions;
          this.loadingInstitution = false;
        })
        .catch(() => {
          this.loadingInstitution = false;
        });
    },
    getStates() {
      if (this.states.length > 0) return Promise.resolve();
      this.loadingState = true;
      return getRequest("/state/")
        .then((response) => {
          this.states = response.states || [];
          this.loadingState = false;
        })
        .catch(() => {
          this.loadingState = false;
        });
    },
    getCities(id) {
      this.loadingCity = true;
      getRequest("/city/", `state_id=${id}`)
        .then((response) => {
          this.cities = response.cities;
          this.loadingCity = false;
        })
        .catch(() => {
          this.loadingCity = false;
        });
    },
    formatDateToDisplay(date) {
      if (!date) return "";
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0"); // 0-indexed
      const year = d.getFullYear();
      return `${year}-${month}-${day}`;
    },
  },
};
</script>
